/**
 * GameBoardRouter - Routes to appropriate game board based on game_id
 *
 * This component detects the current game and renders the appropriate
 * game-specific board component. Supports lazy loading for better performance.
 * Each game board is wrapped in an error boundary for fault isolation.
 */

import { lazy, Suspense, type ReactNode } from "react";
import { GameBoard } from "./GameBoard"; // Classic "terrible_psychiatrist" board
import { GameErrorBoundary } from "./GameErrorBoundary";
import { LoadingWithPersonality } from "./shared/LoadingWithPersonality";
import { logger } from "../lib/logger";
import { useGameStore } from "../store/gameStore";
import type { GameState } from "../types/game";

// Lazy load game boards for better performance
const TerriblePsychiatristBoard = lazy(() =>
  import("./games/terrible_psychiatrist/TerriblePsychiatristBoard").then(
    (module) => ({
      default: module.TerriblePsychiatristBoard,
    }),
  ),
);

const SickDayBoard = lazy(() =>
  import("./games/sick_day/SickDayBoard").then((module) => ({
    default: module.SickDayBoard,
  })),
);

const DebateClubBoard = lazy(() =>
  import("./games/debate_club/DebateClubBoard").then((module) => ({
    default: module.DebateClubBoard,
  })),
);

const PeerReviewBoard = lazy(() =>
  import("./games/peer_review/PeerReviewBoard").then((module) => ({
    default: module.PeerReviewBoard,
  })),
);

const CawBoard = lazy(() =>
  import("./games/caw/CawBoard").then((module) => ({
    default: module.CawBoard,
  })),
);

const HotPotatoHeistBoard = lazy(() =>
  import("./games/hot_potato_heist/HotPotatoHeistBoard").then((module) => ({
    default: module.HotPotatoHeistBoard,
  })),
);

const CodebreakersBoard = lazy(() =>
  import("./games/codebreakers/CodebreakersBoard").then((module) => ({
    default: module.CodebreakersBoard,
  })),
);

const LiarsHandBoard = lazy(() =>
  import("./games/liars_hand/LiarsHandBoard").then((module) => ({
    default: module.LiarsHandBoard,
  })),
);

const BluffaloBoard = lazy(() =>
  import("./games/bluffalo/BluffaloBoard").then((module) => ({
    default: module.BluffaloBoard,
  })),
);

const HiveMindBoard = lazy(() =>
  import("./games/hive_mind/HiveMindBoard").then((module) => ({
    default: module.HiveMindBoard,
  })),
);

const DixitBoard = lazy(() =>
  import("./games/dixit/DixitBoard").then((module) => ({
    default: module.DixitBoard,
  })),
);

const DialInBoard = lazy(() =>
  import("./games/dial_in/DialInBoard").then((module) => ({
    default: module.DialInBoard,
  })),
);

const BattleshipBoard = lazy(() =>
  import("./games/battleship/BattleshipBoard").then((module) => ({
    default: module.BattleshipBoard,
  })),
);

const OutOfSpecBoard = lazy(() =>
  import("./games/out_of_spec/OutOfSpecBoard").then((module) => ({
    default: module.OutOfSpecBoard,
  })),
);

// Loading fallback component with personality
function LoadingBoard() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingWithPersonality context="game" />
    </div>
  );
}

// Helper component that wraps game boards with both Suspense and ErrorBoundary
interface GameBoardWrapperProps {
  children: ReactNode;
  gameId: string;
  sessionId: string;
}

function GameBoardWrapper({ children, gameId, sessionId }: GameBoardWrapperProps) {
  return (
    <GameErrorBoundary gameId={gameId} sessionId={sessionId}>
      <Suspense fallback={<LoadingBoard />}>{children}</Suspense>
    </GameErrorBoundary>
  );
}

export function GameBoardRouter() {
  const { sessionId, roomCode, playerId, gameState } = useGameStore();
  const state = gameState as GameState | null;

  if (!sessionId || !playerId || !roomCode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <p className="text-xl text-red-400">
            Session error: Missing session data
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="btn btn-primary mt-4"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!state) {
    return <LoadingBoard />;
  }

  // Detect game
  const gameType = state.game_id || "terrible-psychiatrist";

  // Route to appropriate board based on game (only enabled games)
  switch (gameType) {
    case "terrible-psychiatrist":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <TerriblePsychiatristBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "sick-day":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <SickDayBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "debate-club":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <DebateClubBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "peer-review":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <PeerReviewBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "caw":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <CawBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "hot-potato-heist":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <HotPotatoHeistBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "codebreakers":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <CodebreakersBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "liars-hand":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <LiarsHandBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "bluffalo":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <BluffaloBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "hive-mind":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <HiveMindBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "dixit":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <DixitBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "dial-in":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <DialInBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "battleship":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <BattleshipBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    case "out-of-spec":
      return (
        <GameBoardWrapper gameId={gameType} sessionId={sessionId}>
          <OutOfSpecBoard
            sessionId={sessionId}
            playerId={playerId}
            roomCode={roomCode}
          />
        </GameBoardWrapper>
      );

    // Fallback for games not enabled
    default:
      logger.warn("game_not_enabled", { gameType, fallback: "classic board" });
      return (
        <GameErrorBoundary gameId={gameType} sessionId={sessionId}>
          <GameBoard />
        </GameErrorBoundary>
      );
  }
}
